import React, { useEffect, useState } from 'react';
import { DatePicker } from '@/components/common/DatePicker';
import {
  DropdownComponentWithSearch,
  DropdownComponentWithSearchOption,
} from 'fluent-components';
import { useQuery } from '@apollo/client';
import { RecordTypeQueryResponse } from '@/types/recordManagement';
import { RECORD_TYPES_QUERY } from '@/graphql/queries/record-management';
import { toast } from '@/components/hooks/use-toast';
import { Separator } from '@/components/ui/separator';

export interface RecordFormData {
  recordDate?: Date | null;
  doctorName?: string;
  title?: string;
  nameOnRecord?: string;
  typeOfRecord?: DropdownComponentWithSearchOption;
  organisationName?: string;
}

interface RecordFormProps {
  description?: string;
  setFormData?: (data: RecordFormData) => void;
  formData?: RecordFormData;
  nameOnRecord?: string;
  classifierStatus?: boolean;
  isSupervisior?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  familyMemberList?: any[];
  patientId?: string;
}

const RecordForm: React.FC<RecordFormProps> = ({
  nameOnRecord,
  description,
  formData,
  setFormData,
  classifierStatus,
  familyMemberList,
  patientId,
}) => {
  // Typing the state to clarify what it contains

  const [selectedName, setSelectedName] =
    useState<DropdownComponentWithSearchOption>({
      label: '',
      value: '',
    });
  const [nameList, setNameList] = useState<DropdownComponentWithSearchOption>(
    []
  );
  const { data: recordTypes, error: recordTypesError } =
    useQuery<RecordTypeQueryResponse>(RECORD_TYPES_QUERY);
  const firstRecordType =
    recordTypes?.medicalRecordCategories?.[0]?.compose?.include?.[0]
      ?.concept[0];
  const [selectedRecord, setSelectedRecord] =
    useState<DropdownComponentWithSearchOption>();

  useEffect(() => {
    if (formData?.typeOfRecord) {
      setSelectedRecord(formData?.typeOfRecord);
    } else if (firstRecordType) {
      setSelectedRecord({
        label: firstRecordType?.display,
        value: firstRecordType?.code,
      });
    }

    if (formData?.nameOnRecord) {
      setSelectedName({
        label: formData?.nameOnRecord,
        value: formData?.nameOnRecord,
      });
    }
  }, [formData, firstRecordType]);

  useEffect(() => {
    const updatedNameList = [];
    if (nameOnRecord) {
      updatedNameList.push({ label: nameOnRecord, value: patientId });
    }

    if (familyMemberList && familyMemberList.length > 0) {
      const familyMemberOptions = familyMemberList.map((member) => ({
        label: member.name,
        value: member.id,
      }));

      updatedNameList.push(...familyMemberOptions);
    }
    const uniqueNameList = updatedNameList.filter(
      (item, index, self) =>
        index ===
        self.findIndex((t) => t.label === item.label && t.value === item.value)
    );

    setNameList(uniqueNameList);
  }, [nameOnRecord, familyMemberList]);

  if (recordTypesError) {
    toast({
      title: 'Error',
      description: recordTypesError?.message,
      variant: 'destructive',
    });
  }
  const records =
    recordTypes?.medicalRecordCategories?.[0]?.compose?.include?.[0]?.concept.map(
      (listItem) => ({
        value: listItem.code,
        label: listItem.display,
      })
    ) || [];

  return (
    <div className="w-full p-6 bg-[#F8F8FF] rounded-sm shadow-sm border border-[#DADCFF]">
      {description && (
        <div className="flex flex-col gap-2 mb-5">
          <label className="block text-sm font-medium border-[#CED1D2] text-gray-700">
            Issue description (raised by reviewer)
          </label>
          <div className="border rounded-md border-[#DADCFF] bg-[#F3F3F4] p-2">
            <p className="text-sm break-words overflow-wrap break-word text-[#5B5D5F]">
              {description}
            </p>
          </div>
        </div>
      )}

      <form className="space-y-6">
        <h2 className="text-2xl font-semibold mb-4">Document Type</h2>
        <div className="space-y-1">
          <label className="block text-sm font-medium border-[#CED1D2] text-gray-700">
            Type of record*
          </label>
          <DropdownComponentWithSearch
            items={records} // Assuming DropdownComponentWithSearch expects an array of strings
            selectedItem={selectedRecord}
            setSelectedItem={(item: DropdownComponentWithSearchOption) => {
              setSelectedRecord(item);
              setFormData?.({ ...formData, typeOfRecord: item });
            }}
            enableSearch={false}
          />
        </div>
        {!classifierStatus && (
          <>
            <Separator />

            <h2 className="text-2xl font-semibold mb-4">General Information</h2>

            <div className="space-y-1">
              <label className="text-gray-700 font-medium text-sm mb-1">
                Name on record*
              </label>
              <DropdownComponentWithSearch
                items={nameList} // TO-DO - Integrate List of relative consent user
                setSelectedItem={(item: DropdownComponentWithSearchOption) => {
                  setSelectedName(item);
                  setFormData?.({ ...formData, nameOnRecord: item.label });
                }}
                selectedItem={selectedName}
                enableSearch={true}
              />
            </div>

            <div className="space-y-1">
              <label className="block text-sm font-medium border-[#CED1D2] text-gray-700">
                Date of record*
              </label>
              <DatePicker
                selectedDate={formData?.recordDate || null}
                onSelectDate={(dateValue: Date | null) =>
                  setFormData?.({ ...formData, recordDate: dateValue })
                }
                calendarPosition="top-[-100px]"
              />
            </div>
            <div className="space-y-1 ">
              <label className="block text-sm font-medium text-gray-700 ">
                Referred by doctor
              </label>
              <input
                type="text"
                value={formData?.doctorName || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFormData?.({ ...formData, doctorName: e.target.value })
                }
                maxLength={100}
                className="block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:border-[#CED1D2] sm:text-sm"
              />
            </div>
            <div className="space-y-1 ">
              <label className="block text-sm font-medium text-gray-700 ">
                Organization (Lab Name)
              </label>
              <input
                type="text"
                value={formData?.organisationName || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFormData?.({
                    ...formData,
                    organisationName: e.target.value,
                  })
                }
                maxLength={100}
                className="block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:border-[#CED1D2] sm:text-sm"
              />
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default RecordForm;
